import { useMemo, type ReactNode } from "react";
import dayjs from "dayjs";
import { DataType } from "../../docstore-section-table/model/types";
import { filterFieldMapping, getFieldValue } from "../model/constants";
import { FilterType } from "./use-filters";

interface UseDocstoreFiltersProps {
  activeFilters: FilterType[];
  dataSource: DataType[];
  searchValue: string;
  addFilter: (field: string, label: string) => void;
  removeFilter: (filterId: string) => void;
  updateFilter: (filterId: string, updates: Partial<FilterType>) => void;
  onFiltersChange?: (filters: FilterType[], searchValue: string) => void;
}

export function useDocstoreFilters({
  activeFilters,
  dataSource,
  searchValue,
  addFilter,
  removeFilter,
  updateFilter,
  onFiltersChange,
}: UseDocstoreFiltersProps) {
  const activeFiltersByField = useMemo(() => {
    const map = new Map<string, FilterType>();
    activeFilters.forEach((filter) => {
      map.set(filter.field, filter);
    });
    return map;
  }, [activeFilters]);

  const getOptionsForFilter = (filterLabel: string) => {
    const fieldPath = filterFieldMapping[filterLabel];
    if (!fieldPath) return [];

    const uniqueValues = new Set<string>();
    dataSource.forEach((item) => {
      const value = getFieldValue(item, fieldPath);
      if (value && value.trim()) {
        uniqueValues.add(value);
      }
    });

    return Array.from(uniqueValues)
      .sort()
      .map((value) => ({
        label: value,
        value,
      }));
  };

  const handleFilterChange = (filterLabel: string, value: any) => {
    const fieldPath = filterFieldMapping[filterLabel];
    if (!fieldPath) return;

    const existingFilter = activeFiltersByField.get(fieldPath);

    if (!value) {
      if (existingFilter) {
        removeFilter(existingFilter.id);
        const newFilters = activeFilters.filter(
          (filter) => filter.id !== existingFilter.id,
        );
        onFiltersChange?.(newFilters, searchValue);
      }
      return;
    }

    let processedValue: string;
    if (value && value.$isDayjsObject) {
      processedValue = value.format("DD.MM.YYYY");
    } else if (value instanceof Date) {
      const day = value.getDate().toString().padStart(2, "0");
      const month = (value.getMonth() + 1).toString().padStart(2, "0");
      const year = value.getFullYear();
      processedValue = `${day}.${month}.${year}`;
    } else {
      processedValue = value;
    }

    if (existingFilter) {
      updateFilter(existingFilter.id, { value: processedValue });
      const newFilters = activeFilters.map((filter) =>
        filter.id === existingFilter.id
          ? { ...filter, value: processedValue }
          : filter,
      );
      onFiltersChange?.(newFilters, searchValue);
    } else {
      const newFilter: FilterType = {
        id: `${fieldPath}_${Date.now()}`,
        field: fieldPath,
        label: filterLabel,
        value: processedValue,
        operator: "contains",
      };
      addFilter(fieldPath, filterLabel);
      updateFilter(newFilter.id, { value: processedValue });
      onFiltersChange?.([...activeFilters, newFilter], searchValue);
    }
  };

  const getFilterStyle = (filterLabel: string) => {
    const fieldPath = filterFieldMapping[filterLabel];
    const activeFilter = activeFiltersByField.get(fieldPath);

    return {
      border: activeFilter ? "1px solid #1890ff" : "1px dashed #BCBFC0",
      borderRadius: "10px",
    };
  };

  const getFilterIcon = (
    filterLabel: string,
    CloseIcon: ReactNode,
    PlusIcon: ReactNode,
  ) => {
    const fieldPath = filterFieldMapping[filterLabel];
    const activeFilter = activeFiltersByField.get(fieldPath);
    return activeFilter ? CloseIcon : PlusIcon;
  };

  const getFilterValue = (filterLabel: string) => {
    const fieldPath = filterFieldMapping[filterLabel];
    const activeFilter = activeFiltersByField.get(fieldPath);
    if (
      activeFilter?.value &&
      (filterLabel === "Дата выдачи" || filterLabel === "Срок действия")
    ) {
      return dayjs(activeFilter.value, "DD.MM.YYYY");
    }
    return activeFilter?.value;
  };

  return {
    activeFiltersByField,
    getOptionsForFilter,
    handleFilterChange,
    getFilterStyle,
    getFilterIcon,
    getFilterValue,
  };
}
